import React, { useState, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { GlassCard, GlassCardHeader, GlassCardTitle, GlassCardDescription, GlassCardContent } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { Plus, Settings as SettingsIcon } from "lucide-react";
import { PatchPolicyFormValues } from "../../schema";
import { SmtpConfig } from "@/types/settings";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import CustomerTag from "@/components/CustomerTag";
import { getAllCustomers } from "@/utils/customerLookup";
import { SmtpSettingsForm } from "./SmtpSettingsForm";
import { SettingsNote } from "./SettingsNote";
import { toast } from "sonner";
import { useCompactMode } from "@/hooks/useCompactMode";

const STORAGE_KEYS = {
  SMTP_CONFIGS: 'patching_smtp_configs'
};

interface SettingsProps {
  form: UseFormReturn<PatchPolicyFormValues>;
}

export function Settings({ form }: SettingsProps) {
  const { cx } = useCompactMode();
  // SMTP configs state
  const [smtpConfigs, setSmtpConfigs] = useState<SmtpConfig[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SMTP_CONFIGS);
      return stored ? JSON.parse(stored) : [{
        id: "default",
        name: "Office 365 SMTP",
        customerId: "c1",
        isDefault: true,
        host: "smtp.office365.com",
        port: 587,
        username: "notifications@acmecorp.com",
        fromAddress: "patch-notifications@acmecorp.com"
      }];
    } catch (e) {
      console.error("Error loading SMTP configs from localStorage:", e);
      return [{
        id: "default",
        name: "Office 365 SMTP",
        customerId: "c1",
        isDefault: true,
        host: "smtp.office365.com",
        port: 587,
        username: "notifications@acmecorp.com",
        fromAddress: "patch-notifications@acmecorp.com"
      }];
    }
  });

  const [openSmtpDialog, setOpenSmtpDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SmtpConfig | null>(null);

  // Persist SMTP configs
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SMTP_CONFIGS, JSON.stringify(smtpConfigs));
  }, [smtpConfigs]);

  const addSmtpConfig = (config: SmtpConfig) => {
    if (editingConfig) {
      setSmtpConfigs(smtpConfigs.map(c => c.id === editingConfig.id ? config : c));
      toast.success("SMTP configuration updated successfully");
    } else {
      const newId = `smtp-${Date.now()}`;
      setSmtpConfigs([...smtpConfigs, {
        ...config,
        id: newId,
        isDefault: false
      }]);
      toast.success("SMTP configuration added successfully");
    }
    setOpenSmtpDialog(false);
    setEditingConfig(null);
  };

  const editSmtpConfig = (config: SmtpConfig) => {
    setEditingConfig(config);
    setOpenSmtpDialog(true);
  };

  const getCustomerName = (id: string) => {
    return getAllCustomers().find(c => c.id === id)?.name || "Global";
  };

  return (
    <GlassCard variant="default" hover={false}>
      <GlassCardHeader>
        <GlassCardTitle>Email Configuration</GlassCardTitle>
        <GlassCardDescription>
          Configure email notifications and SMTP settings
        </GlassCardDescription>
      </GlassCardHeader>
      <GlassCardContent className={cx("space-y-8", "space-y-4")}>
        <SettingsNote />
        
        {/* SMTP Settings Section */}
        <section className={cx("space-y-4", "space-y-2")}>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Email (SMTP) Configuration</h3>
            <Dialog open={openSmtpDialog} onOpenChange={setOpenSmtpDialog}>
              <DialogTrigger asChild>
                <Button onClick={() => setEditingConfig(null)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add SMTP Configuration
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle>{editingConfig ? "Edit SMTP Configuration" : "Add SMTP Configuration"}</DialogTitle>
                  <DialogDescription>Configure your SMTP server settings.</DialogDescription>
                </DialogHeader>
                <SmtpSettingsForm 
                  initialData={editingConfig as SmtpConfig} 
                  onSubmit={addSmtpConfig} 
                  onCancel={() => setOpenSmtpDialog(false)} 
                  form={form} 
                />
              </DialogContent>
            </Dialog>
          </div>
          
          <div className={cx("grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3", "grid gap-2 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3")}>
            {smtpConfigs.map(config => (
              <GlassCard key={config.id} variant="subtle" hover={true} className="overflow-hidden">
                <div className={cx("p-4 space-y-2", "p-3 space-y-1")}>
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium text-lg">{config.name}</h3>
                    <Dialog open={openSmtpDialog && editingConfig?.id === config.id} onOpenChange={(open) => {
                      setOpenSmtpDialog(open);
                      if (!open) setEditingConfig(null);
                    }}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" onClick={() => editSmtpConfig(config)}>
                          <SettingsIcon className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px]">
                      <DialogHeader>
                          <DialogTitle>Edit SMTP Configuration</DialogTitle>
                          <DialogDescription>Update your SMTP server settings.</DialogDescription>
                      </DialogHeader>
                        <SmtpSettingsForm 
                          initialData={config} 
                          onSubmit={addSmtpConfig} 
                          onCancel={() => setOpenSmtpDialog(false)} 
                          form={form} 
                        />
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="text-sm text-muted-foreground line-clamp-2">
                    {config.host && <span className="block">{config.host}:{config.port}</span>}
                    {config.fromAddress && <span className="block">From: {config.fromAddress}</span>}
                  </div>
                  <div className="pt-2">
                    <CustomerTag customer={getCustomerName(config.customerId)} />
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </section>
      </GlassCardContent>
    </GlassCard>
  );
}
